import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameStartPayload, ServerMessage } from './useWebSocket';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MatchResult {
  gameId: string;
  opponent: { nickname: string; avatar: string };
  youGoFirst: boolean;
  board: (string | null)[] | null;
  isYourTurn: boolean;
}

export interface UseMatchmakingOptions {
  /** Base URL for the REST API (e.g. /api) */
  apiBaseUrl: string;
  /** Function that returns the current auth access token */
  getAuthToken: () => Promise<string>;
  /** Most recent message received from the WebSocket connection */
  lastMessage: ServerMessage | null;
}

export interface UseMatchmakingReturn {
  /** Enter the matchmaking queue */
  joinQueue: () => Promise<void>;
  /** Cancel matchmaking and leave the queue */
  cancelQueue: () => Promise<void>;
  /** Whether the player is currently searching for an opponent */
  isSearching: boolean;
  /** Elapsed time in seconds since joining the queue */
  elapsedTime: number;
  /** Match result once a game_start event is received; null while searching or idle */
  matchResult: MatchResult | null;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const ELAPSED_INTERVAL_MS = 1000;

// ─── Hook Implementation ────────────────────────────────────────────────────

export function useMatchmaking(options: UseMatchmakingOptions): UseMatchmakingReturn {
  const { apiBaseUrl, getAuthToken, lastMessage } = options;

  const [isSearching, setIsSearching] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  // ─── Elapsed time ticker ────────────────────────────────────────────────

  const startTimer = useCallback(() => {
    setElapsedTime(0);
    intervalRef.current = setInterval(() => {
      if (mountedRef.current) {
        setElapsedTime((prev) => prev + 1);
      }
    }, ELAPSED_INTERVAL_MS);
  }, []);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // ─── Join queue ─────────────────────────────────────────────────────────

  const joinQueue = useCallback(async () => {
    const token = await getAuthToken();

    const response = await fetch(`${apiBaseUrl}/matchmaking/join`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        code: 'MATCHMAKING_ERROR',
        message: 'Failed to join matchmaking queue',
      }));
      throw error;
    }

    const data = await response.json().catch(() => ({} as Record<string, unknown>));

    // Immediate match (e.g. paired with an AI bot) is returned synchronously
    // in the HTTP response — don't wait for the game_start WebSocket event,
    // which can race ahead of the listener for bot matches.
    if (data.status === 'matched' && data.gameId) {
      setMatchResult({
        gameId: data.gameId as string,
        opponent: data.opponent as { nickname: string; avatar: string },
        youGoFirst: Boolean(data.youGoFirst),
        board: (data.board as (string | null)[]) ?? null,
        isYourTurn: Boolean(data.isYourTurn),
      });
      setIsSearching(false);
      return;
    }

    setMatchResult(null);
    setIsSearching(true);
    startTimer();
  }, [apiBaseUrl, getAuthToken, startTimer]);

  // ─── Cancel queue ───────────────────────────────────────────────────────

  const cancelQueue = useCallback(async () => {
    const token = await getAuthToken();

    const response = await fetch(`${apiBaseUrl}/matchmaking/cancel`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        code: 'MATCHMAKING_ERROR',
        message: 'Failed to cancel matchmaking',
      }));
      throw error;
    }

    stopTimer();
    setIsSearching(false);
    setElapsedTime(0);
  }, [apiBaseUrl, getAuthToken, stopTimer]);

  // ─── Listen for game_start WebSocket event ──────────────────────────────

  useEffect(() => {
    if (!lastMessage || !isSearching) return;

    if (lastMessage.event === 'game_start') {
      const payload = lastMessage.payload as GameStartPayload;
      stopTimer();
      setIsSearching(false);
      setMatchResult({
        gameId: payload.gameId,
        opponent: payload.opponent,
        youGoFirst: payload.youGoFirst,
        board: payload.board ?? null,
        isYourTurn: Boolean(payload.isYourTurn),
      });
    }
  }, [lastMessage, isSearching, stopTimer]);

  // ─── Cleanup on unmount ─────────────────────────────────────────────────

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return {
    joinQueue,
    cancelQueue,
    isSearching,
    elapsedTime,
    matchResult,
  };
}
